/// <amd-module name="Artifacts/Package/components/PackageVersionsTable" />
import * as Res from "Artifacts/Package/Resources";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { getPackageVersionMetricsAsync } from "Artifacts/Root/helpers/PackageMetricsHelpers";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import * as RootRes from "Artifacts/Root/Resources";
import { IFeed, IPackage, IPackageVersion } from "Artifacts/Root/types";
import { SkeletalPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import { PackageVersionMetrics } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { useComponentContext } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Link } from "VSSUI/Link";
import { IListSelection, ISimpleListCell } from "VSSUI/List";
import { Pill } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import {
  ColumnSelect,
  ITableBreakpoint,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { getSafeId } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface IPackageVersionsTableItem {
  version: ISimpleListCell;
  views: string[];
  publishDate: Date;
  source: string;
  downloads: number;
  users: number;
  isDeleted: boolean;
  isListed: boolean;
}

const tableBreakpoints: ITableBreakpoint[] = [
  {
    breakpoint: ScreenBreakpoints.xsmall,
    columnWidths: [10, -80, 0, 0, 0, 0, 0],
  },
  {
    breakpoint: ScreenBreakpoints.small,
    columnWidths: [10, -300, -100, -100, -100, 0, 0],
  },
  {
    breakpoint: ScreenBreakpoints.medium,
    columnWidths: [10, -250, -100, -100, -100, -50, -50],
  },
  {
    breakpoint: ScreenBreakpoints.large,
    columnWidths: [10, -175, -100, -100, -100, -50, -50],
  },
];

let columns = [
  new ColumnSelect(),
  {
    id: "version",
    name: Res.PackageDetails_VersionsTable_Version,
    readonly: true,
    renderCell: renderVersion,
    width: new ObservableValue(400),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
  {
    id: "views",
    name: Res.PackageDetails_VersionsTable_Views,
    readonly: true,
    renderCell: renderViews,
    width: new ObservableValue(200),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
  {
    id: "publishDate",
    name: Res.PackageDetails_VersionsTable_Date,
    readonly: true,
    renderCell: renderPublishDate,
    width: new ObservableValue(150),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
  {
    id: "source",
    name: Res.PackageDetails_VersionsTable_Source,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(150),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
  {
    id: "downloads",
    name: Res.PackageDetails_VersionsTable_Downloads,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(150),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
  {
    id: "users",
    name: Res.PackageDetails_VersionsTable_Users,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(150),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  } as ITableColumn<IPackageVersionsTableItem>,
];

export interface IPackageVersionsTableProps {
  feed: IFeed;
  pkg: IPackage;
  versions?: SkeletalPackageVersion[];
  selection: IListSelection;
  onClick: (version: string) => string;
  currentVersion?: IPackageVersion;
  componentState: ComponentState;
}

const tableShimmer = new Array(10).fill(
  new ObservableValue<IPackageVersionsTableItem | undefined>(undefined),
);

const PackageVersionsTable: React.FC<IPackageVersionsTableProps> = ({
  feed,
  pkg,
  versions,
  selection,
  onClick,
  currentVersion,
  componentState,
}) => {
  const { pageContext } = useComponentContext();
  const [packageVersionMetricsMap, setPackageVersionMetrics] = useState<{
    [key: string]: PackageVersionMetrics;
  }>({});

  const tableItems = React.useMemo(() => {
    const getPackageVersionDisplay = (
      version: string,
      currentlySelectedVersion?: string,
    ): string => {
      return version === currentlySelectedVersion
        ? version + Res.CurrentlySelectedVersion
        : version;
    };
    const items = versions
      ? versions.map<IPackageVersionsTableItem>((pkgversion) => ({
          version: {
            text: getPackageVersionDisplay(pkgversion.version, currentVersion?.version),
            href: onClick(pkgversion.version),
          },
          publishDate: pkgversion.publishDate,
          downloads: packageVersionMetricsMap[pkgversion.id]?.downloadCount,
          users: packageVersionMetricsMap[pkgversion.id]?.downloadUniqueUsers,
          views: pkgversion.views,
          source: pkgversion.directUpstreamSourceId
            ? feed.upstreamSources.find((source) => source.id === pkgversion.directUpstreamSourceId)
                ?.name || RootRes.FeedSources_Unknown
            : RootRes.FeedSources_Local,
          isDeleted: pkgversion.isDeleted,
          isListed: pkgversion.isListed,
        }))
      : tableShimmer;
    return new ArrayItemProvider<IPackageVersionsTableItem | undefined>(items);
  }, [feed, versions, onClick, packageVersionMetricsMap, currentVersion, pkg.protocol]);

  React.useEffect(() => {
    selection.clearUnselectable();
    tableItems?.value.forEach((item, index) => {
      if (item?.isDeleted) {
        selection.addUnselectable(index);
      }
    });
  }, [tableItems, selection]);

  useEffect(() => {
    let mounted = true;
    async function getMetrics() {
      const projectId = feed?.project ? feed.project.id : undefined;
      const response = await getPackageVersionMetricsAsync(
        pageContext,
        feed.id,
        pkg.id,
        versions ? versions.map((v) => v.id) : [],
        projectId,
      );
      if (mounted) {
        setPackageVersionMetrics(response);
      }
    }
    getMetrics();

    return () => {
      mounted = false;
    };
  }, [feed, pageContext, pkg.id, versions]);

  const ariaMessageId = "package-versions-aria-description";

  return (
    <DefaultLoader state={componentState}>
      <div className="page-content page-content-top">
        <Card className="flex-grow bolt-table-card" contentProps={{ contentPadding: false }}>
          <div className="visibility-hidden" id={getSafeId(ariaMessageId)}>
            {RootRes.PackageListSelection_ScreenreaderHelp}
          </div>
          <section
            aria-label={Res.PackageVersionTable_PackageList}
            aria-describedby={getSafeId(ariaMessageId)}
          >
            <Table
              tableBreakpoints={tableBreakpoints}
              columns={columns}
              itemProvider={tableItems}
              selection={selection}
            />
          </section>
        </Card>
      </div>
    </DefaultLoader>
  );
};

function renderViews(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageVersionsTableItem>,
  tableItem: IPackageVersionsTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <PillGroup className="flex-row" overflow={PillGroupOverflow.wrap}>
        {tableItem.views.map((view) => (
          <Pill key={view}>{view}</Pill>
        ))}
      </PillGroup>
    </SimpleTableCell>
  );
}

function renderVersion(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageVersionsTableItem>,
  tableItem: IPackageVersionsTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <div className={"flex-column"}>
        <RouterLink
          component={(props) => <Link {...props} subtle={true} />}
          to={tableItem.version.href || ""}
        >
          {!tableItem.isDeleted && tableItem.isListed ? (
            <div>{tableItem.version.text}</div>
          ) : (
            <div>
              <del>{tableItem.version.text}</del>
            </div>
          )}
        </RouterLink>
      </div>
    </SimpleTableCell>
  );
}

function renderPublishDate(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageVersionsTableItem>,
  tableItem: IPackageVersionsTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Ago date={tableItem.publishDate} />
    </SimpleTableCell>
  );
}

export { PackageVersionsTable };
